import { useTheme } from '../context/ThemeContext';
import { Background3D } from '../components/Background3D';
import { Shield, Eye, Lock, UserCheck, Database, Globe } from 'lucide-react';

export function Privacy() {
  const { theme } = useTheme();

  const privacyPrinciples = [
    {
      icon: Shield,
      title: "Data Protection",
      description: "We implement robust security measures to protect your personal information from unauthorized access, alteration, or disclosure."
    },
    {
      icon: Eye,
      title: "Transparency",
      description: "We are clear about what data we collect, how we use it, and with whom we share it. No hidden practices."
    },
    {
      icon: Lock,
      title: "Data Minimization",
      description: "We only collect the personal information that is necessary for the purposes we have identified."
    },
    {
      icon: UserCheck,
      title: "User Control",
      description: "You have the right to access, correct, delete, or export your personal data at any time."
    },
    {
      icon: Database,
      title: "Secure Storage",
      description: "Your data is stored using industry-standard encryption and security protocols."
    },
    {
      icon: Globe,
      title: "Global Compliance",
      description: "We comply with international privacy laws including GDPR, CCPA, and other applicable regulations."
    }
  ];

  return (
    <div className="relative">
      <Background3D />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-semibold mb-8 leading-tight">
              Privacy <span className="text-gradient">Policy</span>
            </h1>
            <p className={`text-xl leading-relaxed max-w-3xl mx-auto ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Your privacy is fundamental to our mission. We are committed to protecting 
              your personal information and being transparent about our data practices.
            </p>
            <div className="mt-8">
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Last updated: September 7, 2025
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Principles */}
      <section className="py-20">
        <div className="max-w-[1440px] mx-auto px-6">
          <h2 className="text-3xl font-semibold mb-12 text-center">
            Our Privacy Principles
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            {privacyPrinciples.map((principle, index) => (
              <div
                key={index}
                className={`p-6 rounded-2xl border transition-all duration-300 hover:scale-105 ${
                  theme === 'dark'
                    ? 'bg-dark-200/50 border-dark-300 hover:border-primary-500'
                    : 'bg-white/50 border-gray-200 hover:border-primary-300'
                } backdrop-blur-sm`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
                  theme === 'dark' ? 'bg-primary-500/20' : 'bg-primary-100'
                }`}>
                  <principle.icon className="w-6 h-6 text-primary-500" />
                </div>
                <h3 className="text-lg font-semibold mb-3">{principle.title}</h3>
                <p className={`text-sm leading-relaxed ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {principle.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Policy */}
      <section className={`py-20 ${
        theme === 'dark' ? 'bg-dark-200/30' : 'bg-gray-50/50'
      }`}>
        <div className="max-w-4xl mx-auto px-6">
          <div className="space-y-12">
            
            {/* Information We Collect */}
            <div className={`p-8 rounded-2xl ${
              theme === 'dark' ? 'bg-dark-200/50' : 'bg-white/70'
            } backdrop-blur-sm`}>
              <h3 className="text-2xl font-semibold mb-6">Information We Collect</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="text-lg font-medium mb-2">Personal Information</h4>
                  <p className={`leading-relaxed ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    We may collect personal information such as your name, email address, and contact details when you interact with our services, sign up for Victor Academy, or contact us.
                  </p>
                </div>
                <div>
                  <h4 className="text-lg font-medium mb-2">Usage Data</h4>
                  <p className={`leading-relaxed ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    We collect information about how you use our website and services, including pages visited, time spent, and interactions with our content.
                  </p>
                </div>
                <div>
                  <h4 className="text-lg font-medium mb-2">Technical Data</h4>
                  <p className={`leading-relaxed ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    We automatically collect certain technical information, including IP address, browser type, device information, and operating system.
                  </p>
                </div>
              </div>
            </div>

            {/* How We Use Information */}
            <div className={`p-8 rounded-2xl ${
              theme === 'dark' ? 'bg-dark-200/50' : 'bg-white/70'
            } backdrop-blur-sm`}>
              <h3 className="text-2xl font-semibold mb-6">How We Use Your Information</h3>
              <ul className="space-y-3">
                {[
                  "To provide and improve our services and products",
                  "To communicate with you about our research and programs",
                  "To personalize your experience with Victor Academy",
                  "To analyze usage patterns and improve our website",
                  "To comply with legal obligations and protect our rights",
                  "To send you important updates and notifications"
                ].map((item, index) => (
                  <li key={index} className={`flex items-start ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    <div className="w-2 h-2 rounded-full bg-primary-500 mt-2 mr-3 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Data Sharing */}
            <div className={`p-8 rounded-2xl ${
              theme === 'dark' ? 'bg-dark-200/50' : 'bg-white/70'
            } backdrop-blur-sm`}>
              <h3 className="text-2xl font-semibold mb-6">Data Sharing and Disclosure</h3>
              <p className={`leading-relaxed mb-4 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              <ul className="space-y-3">
                {[
                  "With your explicit consent",
                  "To comply with legal requirements or court orders", 
                  "To protect our rights, property, or safety",
                  "With trusted service providers who assist us in operating our services",
                  "In connection with a merger, acquisition, or sale of assets"
                ].map((item, index) => (
                  <li key={index} className={`flex items-start ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    <div className="w-2 h-2 rounded-full bg-primary-500 mt-2 mr-3 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Your Rights */}
            <div className={`p-8 rounded-2xl ${
              theme === 'dark' ? 'bg-dark-200/50' : 'bg-white/70'
            } backdrop-blur-sm`}>
              <h3 className="text-2xl font-semibold mb-6">Your Rights</h3>
              <p className={`leading-relaxed mb-4 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                You have the following rights regarding your personal information:
              </p>
              <ul className="space-y-3">
                {[
                  "Access: Request a copy of the personal information we hold about you",
                  "Correction: Request correction of inaccurate or incomplete information",
                  "Deletion: Request deletion of your personal information",
                  "Portability: Request a copy of your data in a machine-readable format",
                  "Objection: Object to processing of your personal information",
                  "Restriction: Request restriction of processing in certain circumstances"
                ].map((item, index) => (
                  <li key={index} className={`flex items-start ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    <div className="w-2 h-2 rounded-full bg-primary-500 mt-2 mr-3 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20">
        <div className="max-w-[1440px] mx-auto px-6 text-center">
          <div className={`rounded-2xl p-12 ${
            theme === 'dark'
              ? 'bg-gradient-to-br from-dark-200 to-dark-300/50'
              : 'bg-gradient-to-br from-gray-50 to-white'
          }`}>
            <h2 className="text-3xl font-semibold mb-4">
              Questions About Privacy?
            </h2>
            <p className={`text-lg mb-8 max-w-2xl mx-auto ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              If you have any questions about this Privacy Policy or our data practices, 
              please don't hesitate to contact us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:privacy@noemaresearch.com"
                className="px-8 py-4 bg-primary-600 text-white rounded-full font-medium hover:bg-primary-700 transition-colors"
              >
                Contact Privacy Team
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
